/**
 * Turns raw push-device state into what the Users listing shows.
 *
 * The question this column answers is "would a broadcast actually reach this
 * person?" — which the Status column does not, and never did. A recent send
 * managed 1 delivered / 3 failed / 3 pruned across 5 recipients; the gap was
 * users with no registered device at all.
 *
 * THE HARD LIMIT, worth stating plainly because it shapes every label below:
 * every push sender in watchlock hard-DELETES a token document when FCM reports
 * it dead (six sites across five files). Once a user uninstalls and we discover
 * it on the next send, nothing records that they ever had a device. So
 * "uninstalled" and "never enabled notifications" are the SAME STATE in
 * Firestore today, and no amount of reading can separate them.
 *
 * We separate them with a proxy instead: has this user ever reported an app
 * version? `lastAppVersion` is written on app launch, so its presence is real
 * evidence the app ran. Its ABSENCE is weak evidence — an old build that
 * predates version reporting also writes nothing — so the two zero states are
 * worded to claim only what each actually supports. PR5 adds a durable marker
 * at the prune sites, at which point this stops being a heuristic.
 *
 * Deliberately NOT used as evidence: Firebase Auth `lastSignInTime`. There is no
 * web client — `fcmTokens` is written from exactly one place, the RN app — so
 * essentially every user has signed in through the app and the field would mark
 * the entire table amber, which is the same as marking none of it.
 */

import type { PushDeviceState } from '../../services/firebase';

export type PushKind =
  /** At least one live device. */
  | 'devices'
  /**
   * No live device, but the app has demonstrably run on one. A real churn
   * signal: something was registered and now nothing is.
   */
  | 'lapsed'
  /**
   * The user said no at the OS prompt (or later revoked it in Settings). The
   * only zero state we KNOW the cause of, rather than infer — and the only one
   * that is a support opportunity rather than a loss.
   */
  | 'declined'
  /**
   * No live device and no evidence of an app launch.
   *
   * NOT "never installed", despite the name — this is absence of evidence. A
   * user who installed, uninstalled, and was never sent a push lands here,
   * because the token is only discovered dead at send time and is hard-deleted
   * the moment it is. So does anyone on a build predating app-version
   * reporting. The rendered label says "no device" precisely because "never"
   * would overclaim. PR5's prune marker is what will let this state actually
   * mean what its name suggests.
   */
  | 'never';

export interface PushBadge {
  kind: PushKind;
  tokenCount: number;
  /** Sorted, unique platforms — [] when there are no devices. */
  platforms: string[];
  /** Compact platform line for the cell, e.g. "iOS · Android". '' when none. */
  platformLabel: string;
  /**
   * What the cell renders. A bare "0" made the two zero states near-identical
   * at a glance, so each carries its meaning as a word.
   *
   * The wording is load-bearing, not decorative. The grey state is NOT
   * "never installed" — see the note on PushKind.never — so it says "no device",
   * which is exactly what we know. Calling it "never" would assert something we
   * cannot support, and would be wrong for the most interesting row it appears
   * on: a paying subscriber who installed, uninstalled, and whose token we never
   * got to discover was dead.
   */
  label: string;
  /** Tooltip: the full story, including what we can and cannot tell. */
  title: string;
}

/** Cell label for each zero state. Devices render their count instead. */
const ZERO_LABELS: Record<'lapsed' | 'never' | 'declined', string> = {
  // A deliberate user choice, not an inference. The bell glyph reads instantly
  // apart from the churn triangle even at a glance down a narrow column.
  declined: '\u{1F515} declined',
  // The triangle carries the alarm, the word carries the meaning. This state is
  // a genuine churn signal: the app demonstrably ran, and now nothing is
  // registered.
  lapsed: '⚠ churned',
  // Deliberately low visual weight — this is an absence of evidence, not a
  // finding, and it should not compete with the amber state for attention.
  never: '— no device',
};

/** Evidence that this user has actually run the app on a device. */
export interface PushEvidence {
  /** Written on app launch by the RN client. Presence is proof; absence is not. */
  lastAppVersion?: string;
  /**
   * Outcome of the OS notification prompt, rewritten every launch.
   *
   * UNDEFINED means unknown (the user has not opened a build that records it),
   * which is NOT the same as 'not_determined'. Collapsing the two would repeat
   * the exact confusion this field exists to remove.
   */
  notificationPermission?: NotificationPermission;
}

/** Mirrors the RN client's mapping of `messaging().requestPermission()`. */
export type NotificationPermission =
  | 'granted'
  | 'denied'
  | 'not_determined'
  | 'provisional';

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
};

/** 'ios' → 'iOS'. Unknown platforms pass through untouched rather than vanishing. */
export function platformLabel(platform: string): string {
  return PLATFORM_LABELS[platform.toLowerCase()] ?? platform;
}

/** Format an ISO timestamp for a tooltip, tolerating junk. */
function formatIso(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

/**
 * Derive the badge for one user.
 *
 * `state` is undefined when the callable returned no entry for this uid, which
 * means zero devices — not "not loaded". Callers that have not fetched yet
 * should not call this.
 */
export function derivePushBadge(
  state: PushDeviceState | undefined,
  evidence: PushEvidence = {},
): PushBadge {
  const devices = state?.devices ?? [];
  const tokenCount = state?.tokenCount ?? devices.length;
  const platforms = state?.platforms ?? [];
  // Named for what it is: the PLATFORM line ("iOS · Android"), distinct from the
  // badge's own `label`, which is what the cell prints.
  const platformsLabel = platforms.map(platformLabel).join(' · ');

  // Checked BEFORE the token count, deliberately, and this is an extension of
  // the brief worth calling out.
  //
  // A user who granted permission, got a token saved, and later switched
  // notifications off in Settings still has their token document: the RN client
  // returns early when permission is not granted, so `saveToken` never runs
  // again and the stale doc is never cleaned up. Nothing prunes it either —
  // pruning only happens when a SEND gets a dead-token error back, and iOS does
  // not report a permission revocation that way.
  //
  // So `denied` with tokens present is a real, reachable state, and it is the
  // single most misleading cell this column could render: "1 iOS" for somebody
  // who cannot receive a push at all. The whole point of the column is whether
  // a broadcast lands, so permission wins over the token count. The platform
  // line is still carried through so the device information is not lost.
  if (evidence.notificationPermission === 'denied') {
    const seen = formatIso(state?.newestUpdatedAt);
    return {
      kind: 'declined',
      tokenCount,
      platforms,
      platformLabel: platformsLabel,
      label: ZERO_LABELS.declined,
      title:
        'Notifications are turned OFF for this user — they declined the OS prompt, ' +
        'or switched notifications off in Settings afterwards. No push will be ' +
        'delivered.' +
        (tokenCount > 0
          ? ` ${tokenCount} token${tokenCount === 1 ? '' : 's'} still on record` +
            (platformsLabel ? ` (${platformsLabel})` : '') +
            (seen ? `, last written ${seen}` : '') +
            ' — stale: nothing prunes a token when permission is revoked.'
          : '')
        ,
    };
  }

  if (tokenCount > 0) {
    const seen = formatIso(state?.newestUpdatedAt);
    const deviceWord = tokenCount === 1 ? 'device' : 'devices';
    return {
      kind: 'devices',
      tokenCount,
      platforms,
      platformLabel: platformsLabel,
      label: String(tokenCount),
      title:
        `${tokenCount} registered ${deviceWord}` +
        (platformsLabel ? ` — ${platformsLabel}` : '') +
        (seen ? ` · token last written ${seen}` : ''),
    };
  }

  // `notificationPermission` being set at all is proof the app ran — it is
  // written inside the startup call — so it counts as launch evidence in its own
  // right, independent of app-version reporting.
  if (evidence.lastAppVersion || evidence.notificationPermission) {
    return {
      kind: 'lapsed',
      tokenCount: 0,
      platforms: [],
      platformLabel: '',
      label: ZERO_LABELS.lapsed,
      title:
        'No device will receive a push. This user has run the app' +
        (evidence.lastAppVersion ? ` (version ${evidence.lastAppVersion})` : '') +
        ', so their token was either pruned as dead — uninstalled — or they signed ' +
        'out everywhere.' +
        (evidence.notificationPermission
          ? ` OS notification permission: ${evidence.notificationPermission}.`
          : ''),
    };
  }

  return {
    kind: 'never',
    tokenCount: 0,
    platforms: [],
    platformLabel: '',
    label: ZERO_LABELS.never,
    title:
      'No device will receive a push, and no app launch has been recorded for this ' +
      'user. Note that an older build that predates app-version reporting also ' +
      'records nothing, so this is not proof the app was never installed.',
  };
}

export type PushFilterKey = 'noPush' | 'declined' | 'ios' | 'android';

export const PUSH_FILTERS: Array<{ key: PushFilterKey; label: string }> = [
  { key: 'noPush', label: 'No push' },
  // Its own chip because it is the actionable segment: a paying subscriber who
  // declined is a support conversation, not a lost cause.
  { key: 'declined', label: 'Declined' },
  { key: 'ios', label: 'iOS' },
  { key: 'android', label: 'Android' },
];

/**
 * Would a broadcast fail to reach this user? True for every zero state AND for
 * a declined user who still carries stale tokens — the token count is not the
 * test, deliverability is.
 */
export function isUnreachable(badge: PushBadge): boolean {
  return badge.tokenCount === 0 || badge.kind === 'declined';
}

/** Does this badge belong in the given filter bucket? */
export function matchesPushFilter(badge: PushBadge, key: PushFilterKey): boolean {
  switch (key) {
    case 'noPush':
      // Every unreachable user — the question is "unreachable", not "why".
      return isUnreachable(badge);
    case 'declined':
      return badge.kind === 'declined';
    case 'ios':
      return badge.platforms.includes('ios');
    case 'android':
      return badge.platforms.includes('android');
    default:
      return false;
  }
}
